import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import client, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Upload, Save, Search, X, Users, ListPlus, Trash2, UploadCloud, Loader2, AlertTriangle, Pencil } from "lucide-react";

const CMS_POLL_INTERVAL_MS = 15000;

export default function ActivationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [act, setAct] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dealersOpen, setDealersOpen] = useState(false);
  const [paramsOpen, setParamsOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deletingBusy, setDeletingBusy] = useState(false);
  const fileRef = useRef(null);

  const load = () => client.get(`/activations/${id}`).then((r) => setAct(r.data));
  useEffect(() => { load(); }, [id]); // eslint-disable-line

  const patch = async (patch) => {
    setSaving(true);
    try {
      const { data } = await client.patch(`/activations/${id}`, patch);
      setAct(data);
      toast.success("Saved");
      return true;
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); return false; }
    finally { setSaving(false); }
  };

  const uploadExcel = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const form = new FormData();
    form.append("file", f);
    setSaving(true);
    try {
      const { data } = await client.post(`/activations/${id}/import-excel`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      await load();
      toast.success(`Imported: ${data.matched_count} dealers, ${data.parameter_names.length} params${data.warnings_count ? ` (${data.warnings_count} warnings)` : ""}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setSaving(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadReport = (reportId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/activations/${id}/import-reports/${reportId}`, "_blank");
  };

  const deleteActivation = async () => {
    setDeletingBusy(true);
    try {
      await client.delete(`/activations/${id}`);
      toast.success("Activation deleted");
      nav("/activations");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
      setDeletingBusy(false);
    }
  };

  if (!act) return <div className="mx-auto max-w-6xl p-6 md:p-10"><Skeleton className="h-96" /></div>;

  return (
    <div className="mx-auto max-w-6xl p-6 md:p-10" data-testid="activation-detail">
      <button data-testid="back-to-activations" onClick={() => nav("/activations")} className="mb-4 inline-flex items-center gap-1 text-xs font-mono uppercase tracking-widest text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-3 w-3" /> Back to Activations
      </button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <p className="data-label mb-2">ACTIVATION</p>
            {act.imported_from_feed && (
              <Badge variant="secondary" className="mb-1" data-testid="activation-feed-import-badge">Feed import</Badge>
            )}
          </div>
          <h1 className="text-3xl font-bold tracking-tight md:text-4xl">{act.name}</h1>
          <p className="mt-1 font-mono text-[10px] text-muted-foreground">{act.id} · owner {act.creator_owner_email || "—"}</p>
          {act.imported_from_feed && (
            <p className="mt-1 flex flex-wrap gap-x-3 font-mono text-[10px] text-muted-foreground">
              <span>type {act.type || "—"}</span>
              <span>feed id {act.id_feed || "—"}</span>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <CmsPushControl act={act} onActUpdate={setAct} />
          {!act.imported_from_feed && (
            <>
              <Button data-testid="import-excel-btn" variant="outline" className="gap-1.5" onClick={() => fileRef.current?.click()} disabled={saving}>
                <Upload className="h-4 w-4" /> Import Excel
              </Button>
              <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={uploadExcel} data-testid="import-excel-input" />
            </>
          )}
          <Button data-testid="delete-activation-btn" variant="outline" className="gap-1.5 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => setDeleting(true)} disabled={saving}>
            <Trash2 className="h-4 w-4" /> Delete
          </Button>
        </div>
      </div>

      {/* Basic fields */}
      <BasicFieldsCard act={act} patch={patch} saving={saving} />

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card className="p-6" data-testid="dealers-card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2"><Users className="h-4 w-4" /> Participating dealers</h2>
            {!act.imported_from_feed && (
              <Button size="sm" variant="outline" onClick={() => setDealersOpen(true)} data-testid="pick-dealers-btn">Bulk site selection…</Button>
            )}
          </div>
          <p className="font-mono text-3xl tabular-nums" data-testid="dealers-count">{act.dealer_ids?.length ?? 0}</p>
          <p className="text-xs text-muted-foreground">dealers participate in this activation</p>
        </Card>
        <Card className="p-6" data-testid="params-card">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2"><ListPlus className="h-4 w-4" /> Parameter names</h2>
            {!act.imported_from_feed && (
              <Button size="sm" variant="outline" onClick={() => setParamsOpen(true)} data-testid="edit-params-btn">Edit list…</Button>
            )}
          </div>
          <p className="font-mono text-3xl tabular-nums" data-testid="params-count">{act.parameter_names?.length ?? 0}</p>
          <div className="mt-2 flex flex-wrap gap-1">
            {(act.parameter_names || []).slice(0, 10).map((p) => (
              <span key={p} className="rounded-full border border-border bg-secondary px-2 py-0.5 font-mono text-[10px] text-muted-foreground">{p}</span>
            ))}
            {(act.parameter_names || []).length > 10 && (
              <span className="font-mono text-[10px] text-muted-foreground">+{(act.parameter_names || []).length - 10} more</span>
            )}
          </div>
        </Card>
      </div>

      {importResult && (
        <Card className="mb-6 border-primary/30 bg-primary/5 p-4" data-testid="import-result">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <p className="font-medium">Import complete — {importResult.matched_count} dealers matched, {importResult.parameter_names.length} parameters imported.</p>
              {importResult.warnings_count > 0 && (
                <p className="mt-1 text-xs text-muted-foreground">
                  {importResult.warnings_count} row(s) could not be matched (match field: <code>{importResult.match_field}</code>).
                </p>
              )}
            </div>
            <div className="flex gap-2">
              {importResult.report_id && (
                <Button size="sm" variant="outline" onClick={() => downloadReport(importResult.report_id)} data-testid="download-warnings-csv">
                  Download warnings CSV
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setImportResult(null)}><X className="h-3 w-3" /></Button>
            </div>
          </div>
        </Card>
      )}

      {/* Values matrix */}
      <ValuesMatrix act={act} />

      <DealerPickerDialog open={dealersOpen} onOpenChange={setDealersOpen} act={act} onSaved={load} />
      <ParamsBulkDialog open={paramsOpen} onOpenChange={setParamsOpen} act={act} onSaved={load} />

      <AlertDialog open={deleting} onOpenChange={(o) => !o && !deletingBusy && setDeleting(false)}>
        <AlertDialogContent data-testid="delete-activation-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &quot;{act.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the activation and its {act.dealer_ids?.length ?? 0} dealer assignments and {act.parameter_names?.length ?? 0} parameter values. External CMS calls referencing this activation will start returning <code>404</code>. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deletingBusy}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={deleteActivation}
              disabled={deletingBusy}
              data-testid="confirm-delete-activation-btn"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function BasicFieldsCard({ act, patch, saving }) {
  const editable = !act.imported_from_feed;
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);

  const start = () => {
    setDraft({
      name: act.name || "",
      active_from: act.active_from || "",
      active_to: act.active_to || "",
      description: act.description || "",
      marvin_node_id: act.marvin_node_id || "",
    });
    setEditing(true);
  };

  const save = async () => {
    const ok = await patch({
      name: draft.name,
      active_from: draft.active_from || null,
      active_to: draft.active_to || null,
      description: draft.description,
      marvin_node_id: draft.marvin_node_id,
    });
    if (ok) setEditing(false);
  };

  const v = (k) => (editing ? draft?.[k] ?? "" : act[k] || "");

  return (
    <Card className="mb-6 p-6" data-testid="basic-fields">
      <div className="mb-3 flex items-start justify-between">
        <h2 className="text-lg font-semibold">Details</h2>
        {editable && (
          !editing ? (
            <Button data-testid="edit-basic-fields-btn" variant="ghost" size="sm" className="gap-1.5" onClick={start}>
              <Pencil className="h-3.5 w-3.5" /> Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancel</Button>
              <Button data-testid="save-basic-fields-btn" size="sm" onClick={save} disabled={saving} className="gap-1.5">
                <Save className="h-3.5 w-3.5" /> Save
              </Button>
            </div>
          )
        )}
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
        <div>
          <label className="data-label mb-1 block">Name</label>
          <Input
            data-testid="activation-name"
            disabled={!editing || saving}
            value={v("name")}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </div>
        <div>
          <label className="data-label mb-1 block">Active from</label>
          <Input
            data-testid="activation-from"
            type="date"
            disabled={!editing || saving}
            value={v("active_from")}
            onChange={(e) => setDraft({ ...draft, active_from: e.target.value })}
          />
        </div>
        <div>
          <label className="data-label mb-1 block">Active to</label>
          <Input
            data-testid="activation-to"
            type="date"
            disabled={!editing || saving}
            value={v("active_to")}
            onChange={(e) => setDraft({ ...draft, active_to: e.target.value })}
          />
        </div>
        <div>
          <label className="data-label mb-1 block">Marvin node ID</label>
          <Input
            data-testid="activation-marvin-node-id"
            disabled={!editing || saving}
            value={v("marvin_node_id")}
            onChange={(e) => setDraft({ ...draft, marvin_node_id: e.target.value })}
          />
        </div>
      </div>
      <div className="mt-3">
        <label className="data-label mb-1 block">Description</label>
        <Textarea
          data-testid="activation-description"
          rows={2}
          disabled={!editing || saving}
          value={v("description")}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
          placeholder="Optional — short summary shown to internal reviewers."
        />
      </div>
    </Card>
  );
}

function CmsPushControl({ act, onActUpdate }) {
  const [pushing, setPushing] = useState(false);
  const [pollError, setPollError] = useState(null);
  const prevPollErrorRef = useRef(null);
  const intervalRef = useRef(null);

  const status = act.cms_sync_status;
  const inProgress = status === "initiating" || status === "running";

  const pollStatus = async () => {
    try {
      const { data } = await client.get(`/activations/${act.id}/cms-status`);
      onActUpdate((prev) => ({
        ...prev,
        cms_sync_status: data.cms_sync_status,
        cms_last_synced_at: data.cms_last_synced_at,
        cms_last_error: data.cms_last_error,
      }));
      setPollError(data.poll_error || null);
      if (data.poll_error && !prevPollErrorRef.current) {
        toast.error(`CMS status check failed: ${data.poll_error}`);
      }
      prevPollErrorRef.current = data.poll_error || null;
    } catch (e) {
      // The endpoint itself is our own backend — a failure here means we
      // couldn't even reach it (network/auth), not a CMS-side problem.
      const msg = formatApiError(e?.response?.data?.detail) || "Could not check CMS status";
      setPollError(msg);
      if (!prevPollErrorRef.current) toast.error(msg);
      prevPollErrorRef.current = msg;
    }
  };

  // Poll once on mount / when the activation changes (covers "poll on page load").
  useEffect(() => {
    pollStatus();
    return () => clearInterval(intervalRef.current);
  }, [act.id]); // eslint-disable-line

  // Poll every 15s only while a job is actually in progress.
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (inProgress) {
      intervalRef.current = setInterval(pollStatus, CMS_POLL_INTERVAL_MS);
    }
    return () => clearInterval(intervalRef.current);
  }, [inProgress, act.id]); // eslint-disable-line

  const pushToCms = async () => {
    setPushing(true);
    try {
      const { data } = await client.post(`/activations/${act.id}/push-to-cms`);
      onActUpdate((prev) => ({ ...prev, ...data }));
      prevPollErrorRef.current = null;
      setPollError(null);
      toast.success("Push to CMS started");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Push to CMS failed");
    } finally {
      setPushing(false);
    }
  };

  let caption = "Push to CMS";
  let colorClass = "";
  if (status === "initiating") {
    caption = "Initiating…";
    colorClass = "border-blue-500/40 text-blue-700 dark:text-blue-400";
  } else if (status === "running") {
    caption = "Pushing to CMS…";
    colorClass = "border-blue-500/40 text-blue-700 dark:text-blue-400";
  } else if (status === "finishedok") {
    colorClass = "border-emerald-500/40 text-emerald-700 dark:text-emerald-400";
  } else if (status === "error") {
    colorClass = "border-destructive/40 text-destructive";
  }

  const lastSynced = act.cms_last_synced_at ? new Date(act.cms_last_synced_at).toLocaleString() : "Never synced";

  return (
    <div className="flex items-center gap-2">
      <div className="flex flex-col items-end">
        <p className="font-mono text-[10px] text-muted-foreground" data-testid="cms-last-synced">
          {lastSynced}
        </p>
        {status === "error" && act.cms_last_error && (
          <p className="mt-0.5 max-w-[220px] truncate text-right text-[10px] text-destructive" title={act.cms_last_error}>
            {act.cms_last_error}
          </p>
        )}
      </div>
      <Button
        data-testid="push-to-cms-btn"
        variant="outline"
        className={`gap-1.5 ${colorClass}`}
        onClick={pushToCms}
        disabled={pushing || inProgress}
      >
        {pushing || inProgress ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud className="h-4 w-4" />}
        {caption}
      </Button>
      {pollError && (
        <AlertTriangle
          className="h-4 w-4 shrink-0 text-amber-600"
          data-testid="cms-poll-error-icon"
          title={`CMS status check failed: ${pollError}`}
        />
      )}
    </div>
  );
}

function ValuesMatrix({ act }) {
  const dealerIds = act.dealer_ids || [];
  const params = act.parameter_names || [];
  const values = act.dealer_parameter_values || {};
  const [dealers, setDealers] = useState({});

  useEffect(() => {
    if (dealerIds.length === 0) return;
    (async () => {
      // Fetch dealer names for the ids we have (max ~1000 rows — OK for MVP)
      const { data } = await client.get("/dealers", { params: { all: true } });
      const byId = Object.fromEntries((data.items || []).map((d) => [d.id, d]));
      setDealers(byId);
    })();
  }, [dealerIds.length]); // eslint-disable-line

  if (dealerIds.length === 0 || params.length === 0) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground" data-testid="values-empty">
        Add participating dealers and parameter names (or import an Excel) to see the values matrix.
      </Card>
    );
  }

  const rows = dealerIds.slice(0, 200);
  return (
    <Card className="overflow-hidden p-0" data-testid="values-matrix">
      <div className="overflow-auto" style={{ maxHeight: "70vh" }}>
        <table className="min-w-full text-xs">
          <thead className="thead sticky top-0 z-10 bg-secondary">
            <tr>
              <th className="whitespace-nowrap px-3 py-2 text-left">Dealer</th>
              {params.map((p) => (
                <th key={p} className="whitespace-nowrap px-3 py-2 text-left font-mono normal-case">{p}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((did) => {
              const d = dealers[did];
              const dv = values[did] || {};
              return (
                <tr key={did} className="border-t border-border" data-testid={`values-row-${did}`}>
                  <td className="whitespace-nowrap px-3 py-1.5 font-mono text-[11px]">
                    <div className="font-medium">{d?.name || did.slice(0, 8)}</div>
                    <div className="text-[9px] text-muted-foreground">{d?.company_id}</div>
                  </td>
                  {params.map((p) => (
                    <td key={p} className="whitespace-nowrap px-3 py-1.5 font-mono tabular-nums">{dv[p] ?? ""}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {dealerIds.length > 200 && (
        <p className="border-t border-border px-3 py-2 text-[10px] text-muted-foreground">
          Showing first 200 of {dealerIds.length} rows.
        </p>
      )}
    </Card>
  );
}

function DealerPickerDialog({ open, onOpenChange, act, onSaved }) {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set(act.dealer_ids || []));
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setSelected(new Set(act.dealer_ids || [])); }, [open, act]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      const { data } = await client.get("/dealers", { params: q ? { q, limit: 50 } : { limit: 50 } });
      setItems(data.items || []);
    }, 200);
    return () => clearTimeout(t);
  }, [q, open]);

  const toggle = (id) => setSelected((prev) => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });

  const save = async () => {
    setSaving(true);
    try {
      await client.put(`/activations/${act.id}/dealers`, { dealer_ids: Array.from(selected) });
      toast.success("Participating dealers updated");
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="dealer-picker-dialog">
        <DialogHeader><DialogTitle>Bulk site selection</DialogTitle></DialogHeader>
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="picker-search" placeholder="Search dealer name / code…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-9" />
        </div>
        <p className="font-mono text-[10px] text-muted-foreground">{selected.size} selected</p>
        <div className="max-h-96 overflow-auto rounded-md border border-border">
          {items.map((d) => (
            <label key={d.id} className="flex cursor-pointer items-center gap-3 border-b border-border px-3 py-2 hover:bg-muted" data-testid={`picker-row-${d.id}`}>
              <Checkbox checked={selected.has(d.id)} onCheckedChange={() => toggle(d.id)} />
              <div className="flex-1">
                <div className="text-sm font-medium">{d.name}</div>
                <div className="font-mono text-[10px] text-muted-foreground">{d.global_id} · {d.company_id}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="picker-save"><Save className="mr-1.5 h-3.5 w-3.5" /> Save {selected.size}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ParamsBulkDialog({ open, onOpenChange, act, onSaved }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open) setText((act.parameter_names || []).join("\n")); }, [open, act]);

  const save = async () => {
    setSaving(true);
    try {
      const names = text.split(/\r?\n/).map((s) => s.trim()).filter(Boolean);
      await client.put(`/activations/${act.id}/parameters`, { parameter_names: names });
      toast.success(`${names.length} parameters saved`);
      onOpenChange(false);
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="params-dialog">
        <DialogHeader><DialogTitle>Edit parameter names</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">One parameter per line.</p>
        <Textarea data-testid="params-textarea" rows={12} className="font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} placeholder={"Preis_Urlaubscheck\nPreis_Klimaanlagencheck\n…"} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="params-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
